/**
 * Single source of truth for the Tutorials content, rendered by:
 *  - the public Tutorials page (src/app/guides/tutorials/page.tsx)
 *
 * Style follows the Cursor quickstart: short imperative step headings, 1–3
 * sentences per step, the occasional tip/note callout, and a "Next" list at the
 * end of the quickstart. Step bodies use a tiny inline-markdown subset
 * (`**bold**`, `*italic*`, `` `code` ``, `[label](href)`) rendered by
 * `renderTutorialText` in TutorialsContent.
 */

export type TutorialLang = "sl" | "en";

export interface TutorialStep {
  /** Imperative step heading, e.g. "Ustvari račun". */
  title: string;
  /** Step prose (inline markdown). */
  body: string;
  /** Optional callout shown under the step. */
  callout?: { kind: "tip" | "note"; text: string };
}

export interface TutorialGuide {
  /** Anchor id used for #deep-links and the "Next" list. */
  id: string;
  /** Mono eyebrow label, e.g. "VODIČ · Jutranji brief". */
  eyebrow: string;
  title: string;
  intro?: string;
  steps: TutorialStep[];
}

export interface TutorialsCopy {
  eyebrow: string;
  title: { a: string; em: string };
  subtitle: string;
  /** "Next" list label shown after the quickstart. */
  nextLabel: string;
  /** The main sequential walkthrough. */
  quickstart: TutorialGuide;
  /** Focused topic guides. */
  guides: TutorialGuide[];
}

export const TUTORIALS_COPY: Record<TutorialLang, TutorialsCopy> = {
  sl: {
    eyebrow: "POMOČ · NAVODILA",
    title: { a: "Naredi prvi krog — ", em: "korak za korakom" },
    subtitle:
      "Journail je dnevni krog: zjutraj načrtuješ, čez dan delaš po planu, zvečer reflektiraš. Tu te v nekaj minutah popeljemo skozi cel krog, nato pa še po posameznih temah.",
    nextLabel: "Naprej",
    quickstart: {
      id: "quickstart",
      eyebrow: "ZAČETEK · ~5 MINUT",
      title: "Tvoj prvi dan v Journailu",
      steps: [
        {
          title: "Ustvari račun",
          body: "Prijavi se z e-pošto in geslom ali z Googlom. Po registraciji nov račun počaka na kratko odobritev, nato si takoj pripravljen za delo.",
        },
        {
          title: "Nastavi svoj dan",
          body: "V **Nastavitvah** določi *jezik vmesnika*, *jezik vsebine* (v katerem jeziku naj AI piše plane in povzetke — lahko se razlikuje od vmesnika) ter *delovni čas* in časovni pas.",
          callout: {
            kind: "tip",
            text: "Delovni čas Journail uporabi za »še X ur delovnega dne« in za uro, ob kateri prejmeš jutranji in večerni e-mail.",
          },
        },
        {
          title: "Poveži svoja orodja (neobvezno)",
          body: "Če že uporabljaš koledar ali seznam nalog, ju poveži v **Nastavitve → Integracije**: *Google Calendar* (dogodki v koledarju, RSVP, Google Meet) in *Todoist* (naloge se zlijejo z internimi).",
          callout: {
            kind: "note",
            text: "Brez integracij deluje vse normalno — Journail ima svoj seznam nalog in projektov.",
          },
        },
        {
          title: "Načrtuj prvo jutro",
          body: "Odpri **Danes**. Zjutraj te pričaka *Jutranji brief*. Izberi izhodišče — *Cilji najprej*, *Ena globoka naloga* — ali napiši po svoje. AI predlaga prioritete, pomaga izbrati naloge za danes in sestavi plan dneva. Ko ti ustreza, ga potrdi.",
          callout: {
            kind: "tip",
            text: "Zgoraj vidiš korake toka — Prioritete → Pregled in izbor → Moj plan. Vedno se lahko vrneš korak nazaj.",
          },
        },
        {
          title: "Delaj po planu",
          body: "Ko je plan potrjen, se **Danes** spremeni v delovni prostor: kartica plana, seznam aktivnih nalog in nežne usmeritve AI-ja čez dan. Naloge odkljukaš z enim klikom.",
        },
        {
          title: "Zaključi dan",
          body: "Zvečer te Journail povabi k *večernemu pregledu*. AI pregleda, kaj je narejeno, doda kratko refleksijo in zapiše dnevniški vnos. Dan zaključiš — shrani se v arhiv.",
          callout: {
            kind: "tip",
            text: "Iz večernega e-maila te povezava »Zaključi dan« pripelje naravnost v ta pregled.",
          },
        },
      ],
    },
    guides: [
      {
        id: "jutranji-plan",
        eyebrow: "VODIČ · JUTRANJI BRIEF",
        title: "Načrtuj jutro z AI",
        steps: [
          { title: "Začni jutro", body: "Odpri **Danes** zjutraj in izberi izhodišče (chip) ali napiši, na kaj se želiš osredotočiti." },
          { title: "Pojdi skozi korake", body: "Sledi toku *Prioritete → Pregled in izbor nalog → Moj plan*." },
          { title: "Izberi naloge", body: "V koraku izbora odkljukaj naloge, ki spadajo v današnji dan; ostale lahko prestaviš." },
          {
            title: "Potrdi plan",
            body: "Preglej plan in ga **potrdi**. Iz tega nastane dnevniški vnos za danes.",
            callout: { kind: "tip", text: "Plan lahko čez dan tudi popraviš — odpre se isti tok v načinu urejanja." },
          },
        ],
      },
      {
        id: "ai-pomocnik",
        eyebrow: "VODIČ · AI POMOČNIK",
        title: "Pogovarjaj se z AI pomočnikom",
        intro:
          "AI pomočnik je stranski pogovor, dosegljiv z vsake strani. Pozna tvoje cilje, zadnje dnevniške vnose, naloge in koledar, zato lahko odgovarja konkretno.",
        steps: [
          { title: "Piši prosto", body: "Vprašaj karkoli o svojem dnevu, ciljih ali nalogah." },
          {
            title: "Uporabi ukaze",
            body: "Ko želiš dejanje, AI pripravi gumb za en klik: `/task add|edit|complete|delete|move` (naloge), `/goal add|edit|complete|delete` (cilji), `/event add|edit|delete|move` (dogodki), `/note` (beležka), `/journal search` (iskanje), `/today` · `/yesterday` · `/tomorrow` (navigacija).",
          },
          {
            title: "Potrdi predlog",
            body: "Napiši npr. »dodaj nalogo pripravi deck do 11h« in AI ti ponudi `/task add Pripravi deck · today 11:00` v potrditev.",
            callout: { kind: "note", text: "AI ukaza nikoli ne izvede sam — vedno ti pokaže predlog, ti pa ga potrdiš." },
          },
        ],
      },
      {
        id: "cilji",
        eyebrow: "VODIČ · CILJI",
        title: "Postavi in spremljaj cilje",
        steps: [
          { title: "Izberi cikel", body: "Odpri **Cilji**. Cilje vodiš v treh ciklih: *tedenski*, *mesečni*, *letni*." },
          { title: "Dodaj cilj", body: "Dodaj cilj z naslovom, neobveznim opisom in rokom." },
          { title: "Poveži naloge", body: "Poveži naloge s ciljem, da vidiš napredek na enem mestu." },
          {
            title: "Spremljaj status",
            body: "Status premikaj med *aktiven → delno → zaključen*; cilj lahko tudi začasno *paviraš*.",
            callout: { kind: "tip", text: "Cilje preurejaš z vlečenjem; vrstni red se shrani samodejno." },
          },
        ],
      },
      {
        id: "naloge-koledar",
        eyebrow: "VODIČ · NALOGE & KOLEDAR",
        title: "Upravljaj naloge in koledar",
        intro: "Stran **Naloge & koledar** prikaže tri stolpce: *Danes*, *Ta teden*, *Nekoč*.",
        steps: [
          { title: "Hiter vnos", body: "V naslov dodaj `due:2026-06-30`, `time:11:00`, `goal:<cilj>`, `project:<projekt>` ali prioriteto `p1`–`p4`." },
          { title: "Premakni nalogo", body: "Nalogo povleci na drug dan." },
          { title: "Dodaj dogodek", body: "Dogodke iz Google Calendarja vidiš ob nalogah; ustvariš jih z `/event add` v naravnem jeziku (»ponedeljek ob 16:00, lokacija Strelec, meet«)." },
          { title: "Filtriraj", body: "Filtriraj po *Vse · Danes · Ta teden · Nekoč · Opravljeno* ali po projektu oziroma cilju." },
        ],
      },
      {
        id: "dohitevanje",
        eyebrow: "VODIČ · DOHITEVAMO",
        title: "Dohiti nezaključen dan",
        intro: "Če kakšen dan ostane odprt, te Journail naslednjič vpraša, kaj z njim.",
        steps: [
          { title: "Preskoči", body: "**Preskoči in začni z današnjim dnem** — pretekli dan se samodejno zaključi z opombo." },
          {
            title: "Ali zaključi",
            body: "**Zaključi <dan>** — odpre večerni pregled za tisti dan.",
            callout: { kind: "note", text: "Nekaterih dni ni treba zapirati — tudi to je v redu." },
          },
        ],
      },
      {
        id: "arhiv",
        eyebrow: "VODIČ · ARHIV",
        title: "Najdi in izvozi svoj dnevnik",
        steps: [
          { title: "Brskaj po mesecih", body: "Odpri **Dnevnik**. Vnosi so razvrščeni po mesecih s časovnico za hitro navigacijo." },
          { title: "Išči in filtriraj", body: "Išči po vnosih, nalogah in ciljih ali filtriraj po datumskem razponu." },
          {
            title: "Izvozi",
            body: "Za varnostno kopijo izvozi izbrano obdobje v *Markdown*.",
            callout: { kind: "tip", text: "Vse svoje podatke lahko kadar koli izvoziš (JSON) v Nastavitve → Zasebnost." },
          },
        ],
      },
    ],
  },
  en: {
    eyebrow: "HELP · TUTORIALS",
    title: { a: "Run your first loop — ", em: "step by step" },
    subtitle:
      "Journail is a daily loop: plan in the morning, work the plan through the day, reflect in the evening. This walks you through the full loop in a few minutes, then goes deeper by topic.",
    nextLabel: "Next",
    quickstart: {
      id: "quickstart",
      eyebrow: "GET STARTED · ~5 MIN",
      title: "Your first day in Journail",
      steps: [
        {
          title: "Create your account",
          body: "Sign in with email and password or with Google. After signing up, a new account waits for a quick approval, then you're ready to go.",
        },
        {
          title: "Set up your day",
          body: "In **Settings**, set your *interface language*, *content language* (the language the AI writes plans and summaries in — can differ from the interface), and your *work hours* and time zone.",
          callout: {
            kind: "tip",
            text: "Journail uses your work hours for the “X hours left in your day” readout and to time your morning and evening emails.",
          },
        },
        {
          title: "Connect your tools (optional)",
          body: "If you already use a calendar or task list, connect them in **Settings → Integrations**: *Google Calendar* (events in your calendar, RSVP, Google Meet) and *Todoist* (tasks merge with your internal ones).",
          callout: {
            kind: "note",
            text: "Everything works without integrations — Journail has its own tasks and projects.",
          },
        },
        {
          title: "Plan your first morning",
          body: "Open **Today**. In the morning you're greeted by the *Morning Brief*. Pick a starting point — *Goals first*, *One deep task* — or write your own. The AI suggests priorities, helps you pick today's tasks, and drafts a plan. When it looks right, confirm it.",
          callout: {
            kind: "tip",
            text: "The steps show at the top — Priorities → Review & select → My plan. You can always step back.",
          },
        },
        {
          title: "Work the plan",
          body: "Once confirmed, **Today** turns into a workspace: a plan card, your active tasks, and gentle AI nudges through the day. Check tasks off with a single click.",
        },
        {
          title: "Close the day",
          body: "In the evening, Journail invites you to an *evening debrief*. The AI reviews what got done, adds a short reflection, and writes your journal entry. You close the day — it's saved to your archive.",
          callout: {
            kind: "tip",
            text: "The “Close the day” link in your evening email takes you straight into this debrief.",
          },
        },
      ],
    },
    guides: [
      {
        id: "jutranji-plan",
        eyebrow: "GUIDE · MORNING BRIEF",
        title: "Plan a morning with AI",
        steps: [
          { title: "Start the morning", body: "Open **Today** in the morning and pick a starting chip, or type what you want to focus on." },
          { title: "Move through the steps", body: "Follow the flow *Priorities → Review & select tasks → My plan*." },
          { title: "Select tasks", body: "In the select step, check the tasks that belong to today; reschedule the rest." },
          {
            title: "Confirm the plan",
            body: "Review the plan and **confirm** it. This becomes today's journal entry.",
            callout: { kind: "tip", text: "You can revise the plan during the day — the same flow reopens in edit mode." },
          },
        ],
      },
      {
        id: "ai-pomocnik",
        eyebrow: "GUIDE · AI ASSISTANT",
        title: "Talk to the AI Assistant",
        intro:
          "The AI Assistant is a side conversation reachable from every page. It knows your goals, recent journal entries, tasks, and calendar, so it can answer concretely.",
        steps: [
          { title: "Write freely", body: "Ask anything about your day, goals, or tasks." },
          {
            title: "Use slash commands",
            body: "When you want an action, the AI prepares a one-click button: `/task add|edit|complete|delete|move` (tasks), `/goal add|edit|complete|delete` (goals), `/event add|edit|delete|move` (events), `/note` (a note), `/journal search` (search), `/today` · `/yesterday` · `/tomorrow` (navigation).",
          },
          {
            title: "Confirm the suggestion",
            body: "Type e.g. “add a task prep the deck by 11” and the AI offers `/task add Prep the deck · today 11:00` to confirm.",
            callout: { kind: "note", text: "The AI never runs a command on its own — it always shows a suggestion for you to confirm." },
          },
        ],
      },
      {
        id: "cilji",
        eyebrow: "GUIDE · GOALS",
        title: "Set and track goals",
        steps: [
          { title: "Pick a cycle", body: "Open **Goals**. You keep goals in three cycles: *weekly*, *monthly*, *yearly*." },
          { title: "Add a goal", body: "Add a goal with a title, an optional description, and a due date." },
          { title: "Link tasks", body: "Link tasks to a goal to see progress in one place." },
          {
            title: "Track status",
            body: "Move a goal's status across *active → partial → done*; you can also *pause* a goal.",
            callout: { kind: "tip", text: "Reorder goals by dragging; the order saves automatically." },
          },
        ],
      },
      {
        id: "naloge-koledar",
        eyebrow: "GUIDE · TASKS & CALENDAR",
        title: "Manage tasks and calendar",
        intro: "The **Tasks & calendar** page shows three columns: *Today*, *This week*, *Someday*.",
        steps: [
          { title: "Quick add", body: "In the title, add `due:2026-06-30`, `time:11:00`, `goal:<goal>`, `project:<project>`, or a priority `p1`–`p4`." },
          { title: "Move a task", body: "Drag a task to another day." },
          { title: "Add an event", body: "Google Calendar events show next to tasks; create them with `/event add` in natural language (“Monday at 4pm, location Strelec, meet”)." },
          { title: "Filter", body: "Filter by *All · Today · This week · Someday · Done*, or by project or goal." },
        ],
      },
      {
        id: "dohitevanje",
        eyebrow: "GUIDE · CATCHING UP",
        title: "Catch up on an unfinished day",
        intro: "If a day is left open, Journail asks what to do with it next time.",
        steps: [
          { title: "Skip it", body: "**Skip and start today** — the past day is auto-closed with a note." },
          {
            title: "Or wrap it up",
            body: "**Wrap up <day>** — opens the evening debrief for that day.",
            callout: { kind: "note", text: "Some days don't need wrapping up — that's OK too." },
          },
        ],
      },
      {
        id: "arhiv",
        eyebrow: "GUIDE · ARCHIVE",
        title: "Find and export your journal",
        steps: [
          { title: "Browse by month", body: "Open **Journal**. Entries are grouped by month with a timeline rail for quick navigation." },
          { title: "Search and filter", body: "Search across entries, tasks, and goals, or filter by date range." },
          {
            title: "Export",
            body: "Export any period to *Markdown* for a backup.",
            callout: { kind: "tip", text: "You can export all your data (JSON) anytime in Settings → Privacy." },
          },
        ],
      },
    ],
  },
};
